import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, FileSignature, FlaskConical, Play, MessageSquareQuote, CheckCircle2, Phone } from "lucide-react";
import { FEATURES, CONTACT, ABOUT_PILLARS, TRAININGS } from "../data";
import { whatsappLink } from "../whatsapp";
import WhatsAppIcon from "../components/WhatsAppIcon";

const PILLAR_ICONS = {
  "Training Programs": GraduationCap,
  "MoU Partnerships": FileSignature,
  "Research Laboratory": FlaskConical,
  "Video Library": Play,
  Testimonials: MessageSquareQuote,
};

export default function About() {
  return (
    <div data-testid="about-page" className="relative">
      <span className="stamp-num absolute top-6 right-6 md:right-12">N° 01</span>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-12">
        <p className="section-num">
          <strong>01</strong> About us
        </p>
        <div className="grid md:grid-cols-12 gap-10 items-end mt-5">
          <h1 className="md:col-span-9 font-display text-display-xl text-ink tracking-tightest fade-up">
            Central India's
            <br />
            <span className="font-italic text-coral">turnkey solution</span>
            <br className="hidden md:block" />
            <span> research centre.</span>
          </h1>
          <p className="md:col-span-3 text-ink/65 max-w-md text-[15px] leading-relaxed fade-up-d1">
            Organo Mushroom Research &amp; Training Center — for cultivation,
            spawn, products and education.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20 grid md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5 relative">
          <div className="rounded-4xl bg-sageFog/60 border border-sage/20 p-8 md:p-10 shadow-soft">
            <p className="eyebrow">Research + Training</p>
            <h3 className="mt-4 font-display text-[28px] md:text-[32px] text-ink leading-tight tracking-tightest">
              From <span className="font-italic text-coral">spawn lab</span> to your unit — under one roof.
            </h3>
            <ul className="mt-6 space-y-3 text-ink/75 text-[14.5px]">
              <li className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-coral mt-1 flex-shrink-0" /> Sterile spawn production lab</li>
              <li className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-coral mt-1 flex-shrink-0" /> Hands-on training programmes</li>
              <li className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-coral mt-1 flex-shrink-0" /> Project management for setups</li>
              <li className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-coral mt-1 flex-shrink-0" /> Value-added mushroom products</li>
              <li className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-coral mt-1 flex-shrink-0" /> FSSAI certified · Make in India</li>
            </ul>
            <div className="mt-7 inline-flex items-center gap-3 bg-butter text-ink rounded-full px-5 py-2.5 text-[12px] uppercase tracking-[0.22em] font-medium shadow-stamp">
              <GraduationCap className="h-4 w-4" /> Central India
            </div>
          </div>
        </div>

        <div className="md:col-span-7 space-y-5 text-ink/80 leading-relaxed text-[15.5px]">
          <p>
            Organo Mushroom Research &amp; Training Center provides turnkey
            solutions for Mushroom cultivation in central India.
          </p>
          <p>
            Organo Mushroom conducts training programmes on different kinds of
            mushroom — both edible and medicinal.
          </p>
          <p>
            We provide good quality Mushroom Spawn, Mushroom Growing Kits,
            Mushroom Cultivation Chemicals, Mushroom Cultivation Equipment with
            cutting-edge technology and Mushroom Project Management.
          </p>
          <p>
            We trade in many kinds of edible and medicinal mushrooms. Our
            extensive knowledge of the cultivation sector lets us efficiently
            serve a variety of food processing enterprises.
          </p>
          <p>
            Our organisation runs a sound research laboratory and uses
            cutting-edge technologies throughout the production process — from
            growing the mushrooms to shipping the final product.
          </p>
        </div>
      </section>

      {/* Mushroom Training Program */}
      <section
        data-testid="about-training-section"
        className="bg-bone border-y border-ink/10 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="section-num"><strong>·</strong> Programmes</p>
              <h2 className="mt-5 font-display text-display-md text-ink tracking-tightest">
                Mushroom <span className="font-italic text-coral">Training Program</span>
              </h2>
              <p className="mt-4 text-ink/65 text-[15px] max-w-2xl leading-relaxed">
                Three hands-on tracks — from the entrepreneur foundation
                programme to advanced medicinal mushroom cultivation. Certified
                on completion, starter spawn included.
              </p>
            </div>
            <Link
              to="/workshop"
              data-testid="about-training-view-all"
              className="md:col-span-4 md:justify-self-end link-underline inline-flex items-center gap-2 text-ink text-sm font-medium"
            >
              View all programmes <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {TRAININGS.map((t, idx) => (
              <article
                key={t.id}
                data-testid={`about-training-${t.id}`}
                className="bg-boneSoft rounded-4xl border border-ink/5 shadow-soft p-7 md:p-8 flex flex-col lift"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-coral text-[12px] tabular-nums tracking-[0.22em] uppercase">
                    Track 0{idx + 1}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-muted bg-bone rounded-full px-3 py-1 border border-ink/10">
                    {t.badge}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-[22px] text-ink leading-tight tracking-tightest">
                  {t.title}
                </h3>
                <p className="font-italic text-coral text-[15px] mt-1">{t.subtitle}</p>
                <p className="mt-4 text-ink/60 text-[13.5px] leading-relaxed">
                  {t.duration}
                </p>
                <div className="mt-auto pt-6 border-t border-ink/5 flex items-baseline justify-between">
                  <p className="font-display text-[26px] text-ink tabular-nums tracking-tightest">
                    ₹{t.price.toLocaleString("en-IN")}
                  </p>
                  <Link
                    to="/workshop"
                    className="link-underline text-[13px] font-medium text-coral inline-flex items-center gap-1.5"
                  >
                    Details <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-3">
            <a
              href={`tel:+91${CONTACT.phones[1].replace(/\s/g, "")}`}
              className="btn-primary"
            >
              Call to enroll
              <span className="knob">
                <Phone className="h-4 w-4" />
              </span>
            </a>
            <a
              href={whatsappLink("Hi Organo Mushroom 👋\n\nI'd like more details on the training programmes.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-ink link-underline text-sm font-medium self-center"
            >
              <WhatsAppIcon className="h-4 w-4 text-whatsapp" /> WhatsApp us
            </a>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-sageFog border-y border-ink/10 py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <h2 className="md:col-span-8 font-display text-display-md text-ink tracking-tightest">
              Everything under
              <br />
              <span className="font-italic text-coral">one roof.</span>
            </h2>
            <p className="md:col-span-4 text-ink/70 text-[15px] leading-relaxed">
              Training, MoU partnerships, a working research laboratory, a
              growing video library, and real alumni testimonials.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-5 md:gap-6">
            {ABOUT_PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[p.label] || CheckCircle2;
              return (
                <div key={p.label} className="bg-boneSoft rounded-3xl p-6 border border-ink/5 shadow-soft">
                  <Icon className="h-6 w-6 text-coral" />
                  <p className="font-display text-coral text-[12px] mt-3 tabular-nums tracking-[0.22em] uppercase">
                    0{i + 1}
                  </p>
                  <h3 className="font-display text-[20px] mt-1.5 text-ink leading-tight tracking-tightest">
                    {p.label}
                  </h3>
                  <p className="mt-2 text-ink/60 text-[13px] leading-relaxed">{p.note}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick facts */}
      <section className="bg-ink text-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-12">
          {FEATURES.map((f, i) => (
            <div key={f.title}>
              <span className="font-display text-coral text-[40px] leading-none tabular-nums tracking-tightest">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="text-bone font-medium mt-3 text-[15px]">{f.title}</p>
              <p className="text-bone/60 text-sm mt-1.5 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-8 items-center">
        <h3 className="md:col-span-8 font-display text-display-md text-ink tracking-tightest">
          Visit the centre
          <br />
          <span className="font-italic text-coral">in Manish Nagar.</span>
        </h3>
        <div className="md:col-span-4 flex md:justify-end gap-3 flex-wrap">
          <Link to="/contact" className="btn-primary">
            Visit us <span className="knob">→</span>
          </Link>
          <Link to="/workshop" className="link-underline inline-flex items-center gap-2 text-ink text-sm font-medium self-center">
            Training programmes <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
